import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import SearchInput from '../modules/SearchInput';
import { ROLES, RolesLowerCase } from '../../enums/ROLES';
import { getOrganizationUsers, getUserOrganization } from '../../api/organizationsApi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { User } from '../modules/OrganizationCard';
import useDebounce from '../../helpers/useDebounce';
import { getAllRolesThunk } from '../../store/user/userSlice';
import { PaginationFooter } from '../modules/PaginationFooter';
import LinearProgress from '../modules/LinearProgress';
import { SortFields, SortType } from '../../enums/SORT';
import { getAllUsers } from '../../api';
import { USER, USER_NEW } from '../../enums';
import { OrganizationUserSelector } from '../modules/UserSelectItem';
import styles from './UsersList.module.scss';

type TUsersListSelection = {
  setSelectedUser: (user: USER_NEW) => void;
  parentLoading: boolean;
  filterUser: USER;
  currentUserInList: boolean;
  orgId: string;
};

const UsersListSelection: React.FC<TUsersListSelection> = ({ setSelectedUser, parentLoading, filterUser, currentUserInList, orgId }) => {
  const dispatch = useAppDispatch();

  const userRole = useAppSelector((state) => state.user.role);
  const { currentUser } = useAppSelector((state) => state.user);
  const [currentRole, setCurrentRole] = useState<ROLES | 'all'>('all');
  const [searchText, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const debouncedValue = useDebounce<string>(searchText, 500);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState<SortFields>(SortFields.NAME);
  const [sortType, setSortType] = useState<SortType>(SortType.ASC);

  const [usersList, setUsersList] = useState<USER[]>([]);
  const [total, setTotal] = useState<number>(0);

  const fetchData = async () => {
    setLoading(true);
    let rolesQuery: string[] = [];
    if (currentRole === 'all') {
      rolesQuery = [RolesLowerCase.ORGANIZATION_ADMIN, RolesLowerCase.ANALYST];
    } else {
      rolesQuery = [RolesLowerCase[currentRole]];
    }

    try {
      if (userRole === ROLES.SUPER_ADMIN) {
        const res = await getOrganizationUsers({
          page,
          limit: rowsPerPage,
          search: searchText,
          sort: sortType,
          role: rolesQuery,
          sortField,
          orgId,
        });
        // @ts-ignore
        const users = res.data.documents.filter((u: USER) => u?.user?.email !== filterUser.email).filter((u) => !u?.user?.invitation_token);

        setUsersList(users);
        setTotal(res.data.total);
      } else {
        await getUserOrganization().then(() => {
          getOrganizationUsers({ role: rolesQuery, page: page, limit: rowsPerPage, search: searchText, sort: sortType, sortField })
            .then((res) => {
              const target = (filterUser?.email || '').toLowerCase();

              const users = res.data.documents.filter((u: any) => {
                const email = (u?.user?.email ?? u?.email ?? '').toLowerCase();
                const hasInvite = Boolean(u?.user?.invitation_token ?? u?.invitation_token);
                return email !== target && !hasInvite;
              });

              if (currentUserInList) {
                setUsersList(users);
              } else {
                setUsersList(users.filter((u: USER) => u.id !== currentUser?.id));
              }
              setTotal(res.data.total);
            })
            .catch((error) => {
              console.error(error);
            });
        });
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, []);

  useEffect(() => {
    fetchData();
  }, [debouncedValue, currentRole, sortType, page, rowsPerPage]);

  const setRoleFilter = (role: ROLES | 'all') => {
    setPage(0);
    setCurrentRole(role);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  return (
    <div className={styles.container}>
      {loading && <LinearProgress />}
      {parentLoading && <LinearProgress />}
      <div className="mb-5">
        <div className={styles.pageTitle}>Users List</div>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <SearchInput className={styles.search} onChange={onSearch} />
            <div className={styles.toolbarRoles}>
              <div onClick={() => setRoleFilter('all')} className={cx(styles.roleButton, currentRole === 'all' && styles.roleButtonActive)}>
                All
              </div>
              <div
                onClick={() => setRoleFilter(ROLES.ORGANIZATION_ADMIN)}
                className={cx(styles.roleButton, currentRole === ROLES.ORGANIZATION_ADMIN && styles.roleButtonActive)}
              >
                OA
              </div>
              <div
                onClick={() => setRoleFilter(ROLES.ANALYST)}
                className={cx(styles.roleButton, currentRole === ROLES.ANALYST && styles.roleButtonActive)}
              >
                Analyst
              </div>
            </div>
          </div>
        </div>
        <div className={styles.usersList}>
          {usersList.map((user) => (
            // @ts-ignore
            <div className={styles.userItem} key={user.user.id} onClick={() => setSelectedUser(user)}>
              {/* @ts-ignore */}
              <OrganizationUserSelector user={user} fetchData={fetchData} />
            </div>
          ))}
        </div>
        <PaginationFooter
          page={page}
          total={total}
          onPageChange={onPaginate}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsChange}
        />
      </div>
    </div>
  );
};

export default UsersListSelection;
